import { useEffect } from "react";
import { connectSyncSocket, disconnectSyncSocket } from "../api/sync.js";
import { hydrateFromServer, loadAuthFromStorage } from "../api/client.js";
import { listAccounts } from "../api/accounts.ts";
import { state as legacyState } from "../state/store.js";
import type { AppState } from "../types.ts";
import { Login } from "../views/Login.tsx";
import { Onboarding } from "../views/Onboarding.tsx";
import { Shell } from "../views/Shell.tsx";
import { useAppActions } from "./AppProvider.tsx";
import { decideView, setOnboardingActive } from "./view.ts";

export function App() {
  const { state, dispatch } = useAppActions();

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      dispatch({ type: "SET_VIEW", view: "loading" });
      const auth = loadAuthFromStorage();
      if (!auth?.token) {
        dispatch({ type: "SET_VIEW", view: "login" });
        return;
      }
      try {
        await hydrateFromServer();
        if (cancelled) return;
        connectSyncSocket();
        // Carrega contas (Baileys/Unipile) ANTES de decidir a view, senão o
        // decideView só vê whatsappSession e cai em "onboarding" quando o user
        // só tem contas Unipile.
        let accounts: AppState["accounts"] = [];
        try {
          accounts = await listAccounts();
          if (!cancelled) dispatch({ type: "SET_ACCOUNTS", accounts });
        } catch (err) {
          console.warn("[wcrm] listAccounts during bootstrap failed", err);
        }
        if (cancelled) return;
        // legacyState (mutable global from store.js) is missing the multi-account
        // fields que o reducer React adicionou — passa por unknown e injeta as
        // contas na hidratação pra view.ts enxergar.
        const nextState = { ...(legacyState as unknown as AppState), accounts };
        dispatch({ type: "HYDRATE_STATE", payload: nextState });
        dispatch({ type: "SET_VIEW", view: decideView(nextState) });
      } catch (err) {
        console.warn("[wcrm] bootstrap failed", err);
        dispatch({ type: "SET_VIEW", view: "login" });
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
      disconnectSyncSocket();
    };
  }, [dispatch]);

  useEffect(() => {
    if (state.view === "loading") return;
    const next = decideView(state);
    // Encerrou o fluxo de onboarding (pulou ou conectou os 3) — limpa a flag
    // para não puxar o usuário de volta ao onboarding depois.
    if (next === "crm") setOnboardingActive(false);
    if (state.view !== next) dispatch({ type: "SET_VIEW", view: next });
  }, [state, dispatch]);

  // Refetch accounts toda vez que o token mudar (= login de outro user).
  // Esse hook precisa ficar em App.tsx (que está sempre montado), não no
  // AccountSwitcher (que só monta em view=crm). Sem isso, sellers que cairam
  // em onboarding nunca carregariam suas contas e ficariam presos no QR.
  useEffect(() => {
    if (!state.authToken) return;
    let cancelled = false;
    listAccounts()
      .then((accounts) => { if (!cancelled) dispatch({ type: "SET_ACCOUNTS", accounts }); })
      .catch((err) => console.warn("[wcrm] listAccounts on auth-change failed", err));
    return () => { cancelled = true; };
  }, [state.authToken, dispatch]);

  if (state.view === "loading") return <div className="loading-screen">Carregando...</div>;
  if (state.view === "login") return <Login />;
  if (state.view === "onboarding") return <Onboarding />;
  return <Shell />;
}
